import { APIResponse, APIResponseCollection, EStrapiApi } from 'common-types';
import { APP_TYPE, DEFAULT_LOCALE } from 'utils/constants';

import { fetchLocalizedContent } from './helpers/fetch-localized-content';
import { fetchStrapiCollection, fetchStrapiSingle, fetchStrapiSingleType } from './helpers/fetch-strapi-collection';

// --- Shared field/populate constants for REST queries ---

const ARTICLE_FIELDS = ['title', 'subtitle', 'coverSrc', 'isNew', 'publishedAt', 'gifSrc', 'timeToRead', 'searchQuery'];
const ARTICLE_POPULATE = { tags: { fields: ['tag'] } };
const COLLECTION_FIELDS = ['title', 'description', 'image', 'publishedAt', 'gifSrc'];
const CATEGORY_FIELDS = ['title', 'subtitle', 'order', 'view', 'publishedAt'];

export const getArticle = async (
  id: string | null,
  locale: string,
  signal: AbortSignal,
): Promise<APIResponse<'api::article.article'>> => {
  const populate = {
    content: {
      populate: '*',
    },
    categories: {
      fields: '*',
    },
    collections: {
      fields: '*',
    },
    stylist: {
      fields: '*',
    },
  };

  return fetchLocalizedContent({
    endpoint: EStrapiApi.ARTICLE,
    id,
    locale,
    populate,
    signal,
  });
};

export const getHighlights = async (
  id: string | null,
  locale: string,
  signal?: AbortSignal,
): Promise<APIResponse<'api::highlight.highlight'>> => {
  const populate = {
    cover: {
      fields: '*',
    },
    content: {
      populate: '*',
    },
  };

  return fetchLocalizedContent({
    endpoint: EStrapiApi.HIGHLIGHTS,
    id,
    locale,
    populate,
    signal,
  });
};

export const getStyleArticle = async (
  id: string | null,
  locale: string,
  signal: AbortSignal,
): Promise<APIResponse<'api::style-article.style-article'>> => {
  const populate = {
    content: {
      populate: '*',
    },
  };

  return fetchLocalizedContent({
    endpoint: EStrapiApi.STYLE_ARTICLE,
    id,
    locale,
    populate,
    signal,
  });
};

export const getLegalSentence = async (
  id: string | null,
  locale: string,
  signal: AbortSignal,
): Promise<APIResponse<'api::legal-sentence.legal-sentence'>> => {
  return fetchLocalizedContent({
    endpoint: EStrapiApi.LEGAL_SENTENCE,
    id,
    locale,
    populate: undefined,
    signal,
  });
};

export const getStylist = async (
  id: string | null,
  locale: string,
  signal: AbortSignal,
): Promise<APIResponse<'api::stylist.stylist'>> => {
  return fetchLocalizedContent({ endpoint: EStrapiApi.STYLIST, id, locale, populate: undefined, signal });
};

// --- Collection endpoints (paginated) ---

type TGetArticlesParams = {
  page?: number;
  pageSize?: number;
  locale?: string | null;
  filters?: Record<string, unknown>;
  sort?: string | string[];
  signal?: AbortSignal;
  profile?: string;
};

export const getArticles = async ({
  page = 1,
  pageSize = 25,
  locale,
  filters,
  sort = 'createdAt:desc',
  signal,
}: TGetArticlesParams = {}): Promise<APIResponseCollection<'api::article.article'>> => {
  return fetchStrapiCollection<'api::article.article'>({
    endpoint: EStrapiApi.ARTICLE,
    page,
    pageSize,
    locale,
    sort,
    signal,
    filters: {
      ...filters,
      applications: { title: { $eq: APP_TYPE } },
    },
    fields: ARTICLE_FIELDS,
    populate: ARTICLE_POPULATE,
  });
};

type TGetCollectionsParams = {
  page?: number;
  pageSize?: number;
  locale?: string | null;
  filters?: Record<string, unknown>;
  sort?: string | string[];
  signal?: AbortSignal;
  profile?: string;
};

export const getCollections = async ({
  page = 1,
  pageSize = 25,
  locale,
  filters,
  sort = 'createdAt:desc',
  signal,
}: TGetCollectionsParams = {}): Promise<APIResponseCollection<'api::collection.collection'>> => {
  const articleFilters = {
    applications: { title: { $eq: APP_TYPE } },
  };

  return fetchStrapiCollection<'api::collection.collection'>({
    endpoint: EStrapiApi.COLLECTIONS,
    page,
    pageSize,
    locale,
    sort,
    signal,
    filters,
    fields: COLLECTION_FIELDS,
    populate: {
      articles: {
        filters: articleFilters,
        fields: ARTICLE_FIELDS,
        populate: ARTICLE_POPULATE,
      },
    },
  });
};

type TGetCollectionParams = {
  id: string;
  locale?: string | null;
  signal?: AbortSignal;
};

export const getCollection = async ({
  id,
  locale,
  signal,
}: TGetCollectionParams): Promise<APIResponse<'api::collection.collection'>> => {
  return fetchStrapiSingle<'api::collection.collection'>({
    endpoint: EStrapiApi.COLLECTIONS,
    id,
    locale,
    signal,
  });
};

type TGetCategoriesParams = {
  page?: number;
  pageSize?: number;
  locale?: string | null;
  filters?: Record<string, unknown>;
  sort?: string | string[];
  signal?: AbortSignal;
  profile?: string;
};

export const getCategories = async ({
  page = 1,
  pageSize = 100,
  locale,
  sort = 'order:asc',
  signal,
}: TGetCategoriesParams = {}): Promise<APIResponseCollection<'api::category.category'>> => {
  const articleFilters = {
    applications: { title: { $eq: APP_TYPE } },
  };

  const categoryFilters = {
    $or: [
      {
        articles: {
          applications: { title: { $eq: APP_TYPE } },
        },
      },
      {
        collections: {
          articles: {
            applications: { title: { $eq: APP_TYPE } },
          },
        },
      },
    ],
  };

  return fetchStrapiCollection<'api::category.category'>({
    endpoint: EStrapiApi.CATEGORIES,
    page,
    pageSize,
    locale: locale ?? DEFAULT_LOCALE,
    sort,
    signal,
    filters: categoryFilters,
    fields: CATEGORY_FIELDS,
    populate: {
      articles: {
        filters: articleFilters,
        fields: ARTICLE_FIELDS,
        populate: ARTICLE_POPULATE,
      },
      collections: {
        fields: COLLECTION_FIELDS,
        populate: {
          articles: {
            filters: articleFilters,
            fields: ARTICLE_FIELDS,
            populate: ARTICLE_POPULATE,
          },
        },
      },
    },
  });
};

type TGetPersonalizedInsightsCategoriesParams = {
  locale?: string | null;
  signal?: AbortSignal;
  profile?: string;
  personalisationTagFilters: Record<string, unknown>[];
};

export const getPersonalizedInsightsCategories = async ({
  locale,
  signal,
  personalisationTagFilters,
}: TGetPersonalizedInsightsCategoriesParams): Promise<APIResponseCollection<'api::category.category'>> => {
  const articleFilters = {
    applications: { title: { $eq: APP_TYPE } },
  };

  const categoryFilters = {
    $or: [
      {
        articles: {
          applications: { title: { $eq: APP_TYPE } },
        },
      },
      {
        collections: {
          articles: {
            applications: { title: { $eq: APP_TYPE } },
          },
        },
      },
    ],
  };

  const filters = {
    $and: personalisationTagFilters.map((tagFilter) => ({
      personalisation_tags: tagFilter,
    })),
  };

  const response =
    await fetchStrapiCollection<'api::personalized-insights-categories.personalized-insights-categories'>({
      endpoint: EStrapiApi.PERSONALIZED_INSIGHTS_CATEGORIES,
      page: 1,
      pageSize: 100,
      signal,
      filters,
      populate: {
        categories: {
          filters: { $and: [categoryFilters, { locale: { $eq: locale ?? DEFAULT_LOCALE } }] },
          fields: CATEGORY_FIELDS,
          populate: {
            articles: {
              filters: articleFilters,
              fields: ARTICLE_FIELDS,
              populate: ARTICLE_POPULATE,
            },
            collections: {
              fields: COLLECTION_FIELDS,
              populate: {
                articles: {
                  filters: articleFilters,
                  fields: ARTICLE_FIELDS,
                  populate: ARTICLE_POPULATE,
                },
              },
            },
          },
        },
        personalisation_tags: {
          populate: { personalisation_tag_key: { fields: ['name'] } },
        },
      },
    });

  // Extract nested categories from personalized response
  const allCategories = response.data.flatMap((item) => item.categories ?? []);

  return {
    data: allCategories,
    meta: { pagination: { page: 1, pageSize: allCategories.length, pageCount: 1, total: allCategories.length } },
  } as APIResponseCollection<'api::category.category'>;
};

type TGetHomeInsightsCategoriesParams = {
  page?: number;
  pageSize?: number;
  locale?: string | null;
  filters?: Record<string, unknown>;
  sort?: string | string[];
  signal?: AbortSignal;
  profile?: string;
};

export const getHomeInsightsCategories = async ({
  page = 1,
  pageSize = 100,
  locale,
  sort = 'order:asc',
  signal,
}: TGetHomeInsightsCategoriesParams = {}): Promise<
  APIResponseCollection<'api::home-insights-category.home-insights-category'>
> => {
  const articleFilters = {
    applications: { title: { $eq: APP_TYPE } },
  };

  return fetchStrapiCollection<'api::home-insights-category.home-insights-category'>({
    endpoint: EStrapiApi.HOME_INSIGHTS_CATEGORIES,
    page,
    pageSize,
    locale: locale ?? DEFAULT_LOCALE,
    sort,
    signal,
    fields: ['title', 'subtitle', 'order', 'view', 'publishedAt'],
    populate: {
      articles: {
        filters: articleFilters,
        fields: ARTICLE_FIELDS,
        populate: ARTICLE_POPULATE,
      },
      collections: {
        fields: COLLECTION_FIELDS,
        populate: {
          articles: {
            filters: articleFilters,
            fields: ARTICLE_FIELDS,
            populate: ARTICLE_POPULATE,
          },
        },
      },
    },
  });
};

// --- Styling Journey endpoints ---

const STAGE_FIELDS = [
  'stageName',
  'stageTitle',
  'order',
  'coverImageActive',
  'coverImageInactive',
  'miniatureImage',
  'previewPhrase',
  'stageValuesHeader',
  'minimumCompletedStepsCount',
];

const STEP_FIELDS = [
  'sectionIcon',
  'sectionTitle',
  'stepTitle',
  'stepSubtitle',
  'stepImage',
  'stepActionTitle',
  'stepActionURL',
  'stepCompletionTriggerName',
  'stepCompletionTriggerRule',
  'duration',
  'image',
  'tag',
];

const INFO_SCREEN_FIELDS = ['type', 'title', 'subtitle', 'image', 'ctaText'];

const SECTION_FIELDS = ['title', 'showHeader', 'shadowColor', 'primaryColor', 'secondaryColor', 'stepShadowColor'];

const JOURNEY_STAGES_POPULATE = {
  fields: STAGE_FIELDS,
  sort: ['order:asc'],
  populate: {
    stageValues: { fields: ['value', 'icon'] },
    stageSections: {
      fields: SECTION_FIELDS,
      populate: {
        stageSteps: {
          fields: STEP_FIELDS,
          populate: {
            stepIntroScreen: { fields: INFO_SCREEN_FIELDS },
          },
        },
        stageSectionCompleteScreen: { fields: INFO_SCREEN_FIELDS },
        stage_gift: { fields: '*' },
      },
    },
    stageGift: { fields: '*' },
    stageGifts: { fields: '*' },
  },
};

type TGetStylingJourneyParams = {
  locale?: string | null;
  signal?: AbortSignal;
};

export const getStylingJourney = async ({ locale, signal }: TGetStylingJourneyParams = {}): Promise<
  APIResponse<'api::styling-journey.styling-journey'>
> => {
  return fetchStrapiSingleType<'api::styling-journey.styling-journey'>({
    endpoint: EStrapiApi.STYLING_JOURNEY,
    locale: locale ?? DEFAULT_LOCALE,
    signal,
    fields: ['title'],
    populate: {
      journeyStages: JOURNEY_STAGES_POPULATE,
    },
  });
};

type TGetPersonalisedStylingJourneyParams = {
  locale?: string | null;
  signal?: AbortSignal;
  personalisationTagFilters: Record<string, unknown>[];
};

export const getPersonalisedStylingJourney = async ({
  locale,
  signal,
  personalisationTagFilters,
}: TGetPersonalisedStylingJourneyParams): Promise<
  APIResponseCollection<'api::personalised-styling-journey.personalised-styling-journey'>
> => {
  const filters = {
    $and: personalisationTagFilters.map((tagFilter) => ({
      personalisation_tags: tagFilter,
    })),
  };

  return fetchStrapiCollection<'api::personalised-styling-journey.personalised-styling-journey'>({
    endpoint: EStrapiApi.PERSONALISED_STYLING_JOURNEYS,
    page: 1,
    pageSize: 100,
    locale: locale ?? DEFAULT_LOCALE,
    signal,
    filters,
    populate: {
      journey_stages: JOURNEY_STAGES_POPULATE,
      personalisation_tags: {
        populate: { personalisation_tag_key: { fields: ['name'] } },
      },
    },
  });
};

// --- Tags endpoint ---

type TGetTagsParams = {
  locale?: string | null;
  signal?: AbortSignal;
};

export const getTags = async ({ locale, signal }: TGetTagsParams = {}): Promise<
  APIResponseCollection<'api::tag.tag'>
> => {
  return fetchStrapiCollection<'api::tag.tag'>({
    endpoint: EStrapiApi.TAGS,
    page: 1,
    pageSize: 100,
    locale,
    signal,
    fields: ['tag'],
  });
};
